import { useMemo } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { VaryingWidthLabel } from './VaryingWidthLabel';

const Row = styled.div<{
  $isHeader?: boolean;
  $small: boolean;
}>`
  display: flex;
  align-items: stretch;
  height: ${({ $small }) => (!$small ? '20px' : '14px')};
  border-bottom: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};

  ${({ $isHeader, theme }) =>
    $isHeader &&
    css`
      background-color: ${theme.appUI.backgroundColor.MUTED};
    `}

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1 1 0;
  width: 35%;
  padding: 0 10px;
  border-right: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};

  &::before {
    content: '';
    display: block;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
  }
`;

const TextColors = {
  BLUE: '#365ca3',
  ORANGE: '#984e26',
  RED: '#980d27'
};

const Wrapper = styled.div<{
  $isConditionallyFormatted: boolean;
}>`
  flex: 1 1 auto;

  ${({ $isConditionallyFormatted }) =>
    $isConditionallyFormatted &&
    css`
      > div {
        > span > div {
          background: ${({ theme }) => theme.appUI.fontColor.DEFAULT};
          opacity: 0.4;
        }

        /* First row */
        &:nth-child(1) {
          > span {
            &:nth-child(1) {
              background-color: transparent;
              > div {
                opacity: 0.3;
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Second row */
        &:nth-child(2) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#F19556', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#F19556', 0.4)};
              }
              > div {
                background: ${TextColors.ORANGE};
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.3)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Third row */
        &:nth-child(3) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#F19556', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#F19556', 0.4)};
              }
              > div {
                background: ${TextColors.RED};
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.2)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.2)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Fourth row */
        &:nth-child(4) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#F19556', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#F19556', 0.4)};
              }
              > div {
                background: ${TextColors.RED};
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.4)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.6;
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.2)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Fifth row */
        &:nth-child(5) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#ED6F73', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#ED6F73', 0.4)};
              }
              > div {
                background: ${TextColors.RED};
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.3)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.6;
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.3)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.6;
              }
            }
          }
        }

        /* Sixth row */
        &:nth-child(6) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#ED6F73', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#ED6F73', 0.3)};
              }
              > div {
                background: ${TextColors.RED};
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.5)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.5)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.8;
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.5)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.5)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.8;
              }
            }
          }
        }

        /* Seventh row */
        &:nth-child(7) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#ED6F73', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#ED6F73', 0.4)};
              }
              > div {
                background: ${TextColors.RED};
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.5)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.5)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.8;
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.7)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.65)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 1;
              }
            }
          }
        }

        /* Eighth row */
        &:nth-child(8) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#F19556', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#F19556', 0.4)};
              }
              > div {
                background: ${TextColors.ORANGE};
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.3)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.6;
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Ninth row */
        &:nth-child(9) {
          > span {
            &:nth-child(1) {
              background-color: transparent;
              > div {
                opacity: 0.3;
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Tenth row */
        &:nth-child(10) {
          > span {
            &:nth-child(1) {
              background-color: transparent;
              > div {
                opacity: 0.3;
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.1)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Eleventh row */
        &:nth-child(11) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#F19556', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#F19556', 0.4)};
              }
              > div {
                background: #884018;
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.2)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.2)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }

        /* Twelfth row */
        &:nth-child(12) {
          > span {
            &:nth-child(1) {
              background-color: ${rgba('#F19556', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#F19556', 0.4)};
              }
              > div {
                background: #884018;
              }
            }
            &:nth-child(2) {
              background-color: ${rgba('#365ca3', 0.3)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.4)};
              }
              > div {
                background: ${TextColors.BLUE};
                opacity: 0.6;
              }
            }
            &:nth-child(3) {
              background-color: ${rgba('#365ca3', 0.1)};
              &::before {
                box-shadow: 0 0 0 1px ${rgba('#365ca3', 0.2)};
              }
              > div {
                background: ${TextColors.BLUE};
              }
            }
          }
        }
      }
    `}
`;
export interface TableProps {
  columnCount?: number;
  conditionallyFormat?: boolean;
  hideHeader?: boolean;
  rowCount?: number;
  small?: boolean;
}

export const Table: React.ComponentType<TableProps> = React.memo(
  ({
    rowCount = 6,
    columnCount = 3,
    hideHeader = false,
    conditionallyFormat = false,
    small = false
  }) => {
    const headerColumns = useMemo(() => {
      const elements = [];
      for (let i = 0; i < columnCount; i++) {
        elements.push(
          <Cell key={i}>
            <VaryingWidthLabel darkText={true} />
          </Cell>
        );
      }
      return elements;
    }, [columnCount]);

    const columns = useMemo(() => {
      const elements = [];
      for (let i = 0; i < columnCount; i++) {
        elements.push(
          <Cell key={i}>
            <VaryingWidthLabel />
          </Cell>
        );
      }
      return elements;
    }, [columnCount]);

    const rows = useMemo(() => {
      const elements = [];
      for (let i = 0; i < rowCount; i++) {
        elements.push(
          <Row key={i} $small={small}>
            {columns}
          </Row>
        );
      }
      return elements;
    }, [columns, rowCount, small]);

    return (
      <Wrapper $isConditionallyFormatted={conditionallyFormat}>
        {!hideHeader && (
          <Row $isHeader={true} $small={small}>
            {headerColumns}
          </Row>
        )}
        {rows}
      </Wrapper>
    );
  }
);
